import { useEffect } from 'react';
import { Users, Target, Heart, Award } from 'lucide-react';

const About = () => {
  useEffect(() => {
    document.title = 'About Us - FemSportNet';
  }, []);

  const values = [
    {
      icon: <Heart className="h-8 w-8 text-green-500" />,
      title: 'Empowerment',
      description: 'We believe in empowering female athletes to reach their full potential and break barriers in sports.'
    },
    {
      icon: <Users className="h-8 w-8 text-green-500" />,
      title: 'Community',
      description: 'Building strong connections and fostering a supportive community for women in sports worldwide.'
    },
    {
      icon: <Target className="h-8 w-8 text-green-500" />,
      title: 'Excellence',
      description: 'Striving for excellence in everything we do, from our platform to the athletes we serve.'
    },
    {
      icon: <Award className="h-8 w-8 text-green-500" />,
      title: 'Recognition',
      description: 'Ensuring female athletes receive the recognition and opportunities they deserve.'
    }
  ];

  const team = [
    {
      name: 'Sarah Chen',
      role: 'CEO & Founder',
      image: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      bio: 'Former Olympic swimmer turned tech entrepreneur, passionate about creating opportunities for female athletes.'
    },
    {
      name: 'Maria Rodriguez',
      role: 'CTO',
      image: 'https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      bio: 'Software engineer with 10+ years experience building scalable platforms for sports communities.'
    },
    {
      name: 'Jessica Williams',
      role: 'Head of Community',
      image: 'https://images.pexels.com/photos/3764574/pexels-photo-3764574.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      bio: 'Former professional basketball player dedicated to fostering connections in the sports community.'
    },
    {
      name: 'Amanda Johnson',
      role: 'Head of Partnerships',
      image: 'https://images.pexels.com/photos/3764011/pexels-photo-3764011.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      bio: 'Sports marketing expert with extensive experience in athlete sponsorships and brand partnerships.'
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About FemSportNet
            </h1>
            <p className="text-xl text-green-100 mb-8">
              We're on a mission to create the world's premier platform for female athletes, 
              connecting talent with opportunities and building a stronger sports community.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                To empower female athletes worldwide by providing a dedicated platform that connects, 
                inspires, and creates opportunities for success in sports and beyond.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                Why FemSportNet Exists
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Female athletes face unique challenges in gaining visibility, finding opportunities, 
                and building professional networks. Traditional sports platforms often overlook the 
                specific needs of women in sports, leaving talented athletes without the recognition 
                and connections they deserve.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                FemSportNet was created to bridge this gap, providing a space where female athletes 
                can showcase their achievements, connect with peers and professionals, and access 
                opportunities that help them thrive in their sporting careers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Values
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                The principles that guide everything we do
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 p-4 mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Meet Our Team
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Passionate individuals dedicated to supporting female athletes
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden">
                  <div className="h-64 overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                      {member.name}
                    </h3>
                    <p className="text-green-500 font-medium mb-3">
                      {member.role}
                    </p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {member.bio}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Our Impact
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Building a stronger community for female athletes worldwide
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-green-500 mb-2">15K+</p>
                <p className="text-gray-600 dark:text-gray-300">Active Athletes</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-green-500 mb-2">50+</p>
                <p className="text-gray-600 dark:text-gray-300">Sports Represented</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-green-500 mb-2">100+</p>
                <p className="text-gray-600 dark:text-gray-300">Countries</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-green-500 mb-2">5K+</p>
                <p className="text-gray-600 dark:text-gray-300">Opportunities Created</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;